import { useState, useCallback, useEffect } from 'react';
import {
  Typography,
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { alpha } from '@mui/material/styles';
import Grid from '@mui/material/Grid2';
import DataGrid from './ui/DataTable';
import Fab from '@mui/material/Fab';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';

// File icons
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import TableChartIcon from "@mui/icons-material/TableChart";
import ArticleIcon from "@mui/icons-material/Article";

import { formatBytes, truncateFileName, getMimeType, getFileIcon } from "../utils/fileUtils";

import axiosInstance from './axiosInstance';

// Breadcrumbs
import { useBreadcrumbs } from './BreadcrumbBar';
import EmptyState from './ui/EmptyState';

// Leerzustand für ein leeres Such-/Filterergebnis (D4)
const NoResultsOverlay = () => (
  <EmptyState title="Nichts gefunden." hint="Such- oder Filterkriterien anpassen." />
);

const OBJECT_TYPES = [
  { value: 'contract', label: 'Vertrag', icon: <ArticleOutlinedIcon />, color: '#2563EB' },
  { value: 'lead', label: 'Lead', icon: <PersonSearchOutlinedIcon />, color: '#2563EB' },
];

const fileIconMap = {
  image: <ImageIcon fontSize="small" />,
  pdf: <PictureAsPdfIcon fontSize="small" />,
  text: <DescriptionIcon fontSize="small" />,
  excel: <TableChartIcon fontSize="small" />,
  word: <ArticleIcon fontSize="small" />,
  file: <InsertDriveFileIcon fontSize="small" />,
};


export default function FileUpload({ onUploadSuccess }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Upload Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [description, setDescription] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  // Edit Dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAttachment, setEditingAttachment] = useState(null);
  const [editDescription, setEditDescription] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Objektzuweisung
  const [selectedObjectType, setSelectedObjectType] = useState('');
  const [selectedObjectId, setSelectedObjectId] = useState('');
  const [objectList, setObjectList] = useState([]);
  const [objectListLoading, setObjectListLoading] = useState(false);

  // Vorschau
  const [previewFile, setPreviewFile] = useState(null);

  // Breadcrumbs
  useBreadcrumbs(
       [
          { label: 'Dateien' },
        ]
  );

  useEffect(() => { fetchAttachments(); }, []);

  useEffect(() => {
    if (!selectedObjectType) {
      setObjectList([]);
      setSelectedObjectId('');
      return;
    }
    const fetchObjects = async () => {
      setObjectListLoading(true);
      try {
        const res = await axiosInstance.get(`/${selectedObjectType}s/`);
        setObjectList(res.data);
      } catch (err) {
        console.error('Fehler beim Laden der Objekte:', err);
        setObjectList([]);
      } finally {
        setObjectListLoading(false);
      }
    };
    fetchObjects();
  }, [selectedObjectType]);

  useEffect(() => {
    return () => { if (previewFile?.blobUrl) window.URL.revokeObjectURL(previewFile.blobUrl); };
  }, [previewFile]);

  const fetchAttachments = async () => {
    try {
      const response = await axiosInstance.get('/attachments/');
      setAttachments(response.data);
    } catch (err) {
      console.error('Fehler beim Laden der Dateien:', err);
    } finally {
      setLoading(false);
    }
  };

  const openDialog = () => {
    setPendingFile(null);
    setDescription('');
    setSelectedObjectType('');
    setSelectedObjectId('');
    setError(null);
    setDialogOpen(true);
  };

  const handleFileSelected = (file) => {
    if (!file) return;
    setPendingFile(file);
  };

  const handleDialogConfirm = async () => {
    if (!pendingFile) return;
    setDialogOpen(false);
    await uploadFile(pendingFile, description, selectedObjectType || null, selectedObjectId || null);
    setPendingFile(null);
    setDescription('');
    setSelectedObjectType('');
    setSelectedObjectId('');
  };

  const handleDialogCancel = () => {
    setDialogOpen(false);
    setPendingFile(null);
    setDescription('');
    setSelectedObjectType('');
    setSelectedObjectId('');
    setError(null);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelected(file);
  }, []);

  const uploadFile = async (file, desc = '', objType = null, objId = null) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', desc);
    if (objType && objId) {
      formData.append('object_type', objType);
      formData.append('object_id', objId);
    }
    setUploading(true);
    setError(null);
    try {
      const res = await axiosInstance.post('/attachments/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAttachments((prev) => [res.data, ...prev]);
      onUploadSuccess?.(res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  // Edit handlers
  const openEditDialog = (row) => {
    setEditingAttachment(row);
    setEditDescription(row.description || '');
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingAttachment) return;
    setEditSaving(true);
    try {
      const formData = new FormData();
      formData.append('description', editDescription);
      const res = await axiosInstance.patch(`/attachments/${editingAttachment.id}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAttachments((prev) =>
        prev.map((a) => (a.id === editingAttachment.id ? { ...a, description: res.data.description } : a))
      );
      setEditDialogOpen(false);
      setEditingAttachment(null);
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
    } finally {
      setEditSaving(false);
    }
  };

  const handleEditCancel = () => {
    setEditDialogOpen(false);
    setEditingAttachment(null);
    setEditDescription('');
  };

  const handleDeleteAttachment = async (id) => {
    if (!window.confirm('Möchten Sie die Datei wirklich löschen?')) return;
    try {
      await axiosInstance.delete(`/attachments/${id}/`);
      setAttachments((prev) => prev.filter((a) => a.id !== id));
      if (previewFile?.id === id) setPreviewFile(null);
    } catch (err) {
      console.error('Fehler beim Löschen:', err);
    }
  };

  const downloadAttachment = async (id, name) => {
    try {
      const res = await axiosInstance.get(`/attachments/${id}/download/`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', name || `attachment_${id}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Fehler beim Herunterladen:', err);
    }
  };

  const openPreview = async (row) => {
    const PREVIEWABLE_TYPES = new Set(['application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml', 'text/plain']);
    const mimeType = getMimeType(row.name);
    const isPreviewable = PREVIEWABLE_TYPES.has(mimeType);
    
    if (!isPreviewable) {
      if (previewFile?.blobUrl) window.URL.revokeObjectURL(previewFile.blobUrl);
      setPreviewFile({ id: row.id, name: row.name, mimeType, blobUrl: null });
      return;
    }
    
    try {
      const res = await axiosInstance.get(`/attachments/${row.id}/download/`, { responseType: 'blob' });
      const serverMimeType = res.headers['content-type']?.split(';')[0] || mimeType;
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: serverMimeType }));
      if (previewFile?.blobUrl) window.URL.revokeObjectURL(previewFile.blobUrl);
      setPreviewFile({ id: row.id, name: row.name, mimeType, blobUrl });
    } catch (err) {
      console.error('Fehler beim Öffnen:', err);
    }
  };

  const getObjectLabel = (obj) => {
    if (selectedObjectType === 'contract') return obj.title || obj.name || `Vertrag #${obj.id}`;
    return obj.name || obj.title || `#${obj.id}`;
  };

  const attachmentColumns = [
    { field: 'id', headerName: 'ID', width: 50 },
    {
      field: 'actions', headerName: 'Aktionen', width: 170,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Tooltip title="Öffnen">
            <IconButton variant="action-edit" onClick={() => openPreview(params.row)}>
              <FileOpenIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Herunterladen">
            <IconButton variant="action-edit" onClick={() => downloadAttachment(params.row.id, params.row.name)}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Bearbeiten">
            <IconButton variant="action-edit" onClick={() => openEditDialog(params.row)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Löschen">
            <IconButton variant="action-delete" onClick={() => handleDeleteAttachment(params.row.id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "name",
      headerName: "Datei",
      flex: 2,
      renderCell: (params) => {
        const mimeType = getMimeType(params.value);
        const iconType = getFileIcon(mimeType);
        return (
          <Tooltip title={params.value}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                {fileIconMap[iconType]}
                {truncateFileName(params.value)}
            </Box>            
          </Tooltip>
        );
      },
    },
    { field: 'description', headerName: 'Beschreibung', flex: 2 },
    {
      field: "size",
      headerName: "Größe",
      flex: 1,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => formatBytes(value),
    },
    {
      field: 'related_object_info', headerName: 'Verknüpfter Typ', flex: 1,
      renderCell: (row) => {
        if (!row.value?.type) return "-";
        const typeConfig = OBJECT_TYPES.find(t => t.value === row.value?.type);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={row.value?.name ?? ''}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {typeConfig?.icon}
                <span style={{ marginLeft: 4 }}>{typeConfig?.label}</span>
              </Box>
            </Tooltip>
          </Box>
        );
      }
    },
    {
      field: 'uploaded_at', headerName: 'Hochgeladen', flex: 1,
      valueFormatter: (value) => {
        if (!value) return '';
        const date = new Date(value);
        const pad = (n) => String(n).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
      },
    },

  ];

  const renderPreview = () => {
    if (!previewFile) return null;
    const { name, mimeType, blobUrl } = previewFile;
    
    let content;
    if (!blobUrl) {
      content = (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Keine Vorschau verfügbar
          </Typography>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => downloadAttachment(previewFile.id, previewFile.name)}
          >
            Datei herunterladen
          </Button>
        </Box>
      );
    } else if (mimeType.startsWith('image/')) {
      content = <img src={blobUrl} alt={name} style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', display: 'block', margin: '0 auto' }} />;
    } else if (mimeType === 'application/pdf' || mimeType.startsWith('text/')) {
      content = <iframe src={blobUrl} title={name} style={{ width: '100%', height: '70vh', border: 'none', background: '#fff' }} />;
    }
    
    return (
      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Tooltip title={name}>
            <Typography variant="subtitle1" fontWeight={500} noWrap sx={{ maxWidth: '80%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {name}
            </Typography>
          </Tooltip>
          <IconButton size="small" onClick={() => setPreviewFile(null)}><CloseIcon fontSize="small" /></IconButton>
        </Box>
        {content}
      </Paper>
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid size={12}><Typography variant="h4" component="h2">Dateien</Typography></Grid>
      <Grid size={12}>
        <DataGrid
          rows={attachments}
          columns={attachmentColumns}
          loading={loading}
          pageSize={5}
          rowsPerPageOptions={[5]}
          autoHeight
          slots={{
            noRowsOverlay: () => (
              <EmptyState
                title="Noch keine Dateien."
                hint="Der Upload nimmt PDFs, Bilder und mehr."
                actionLabel="Datei hochladen"
                onAction={openDialog}
              />
            ),
            noResultsOverlay: NoResultsOverlay,
          }}
          sx={{
            cursor: 'default',
            // Platz für den Leerzustand inkl. Aktion (Default: 2 Zeilenhöhen)
            '--DataGrid-overlayHeight': '180px',
          }}
        />
      </Grid>

      {previewFile && <Grid size={12}>{renderPreview()}</Grid>}

      {/* FAB öffnet Upload-Dialog */}
      <Fab
        color="primary"
        sx={{ position: 'fixed', bottom: 24, right: 24 }}
        onClick={openDialog}
      >
        <AddIcon />
      </Fab>

      {/* Upload-Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Datei hochladen</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>

            <input
              type="file"
              id="file-input"
              style={{ display: 'none' }}
              onChange={(e) => handleFileSelected(e.target.files[0])}
            />
            <Box
              component="label"
              htmlFor="file-input"
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              sx={(theme) => ({
                border: `2px dashed ${isDragging ? theme.palette.primary.main : pendingFile ? theme.palette.success.main : theme.palette.tokens.chartNeutral}`,
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                background: isDragging
                  ? alpha(theme.palette.primary.main, 0.08)
                  : pendingFile
                    ? alpha(theme.palette.success.main, 0.08)
                    : theme.palette.tokens.tableHead,
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
              })}
            >
              <UploadFileIcon sx={{ fontSize: 40, color: pendingFile ? 'success.main' : isDragging ? 'primary.main' : 'text.disabled' }} />
              {pendingFile ? (
                <>
                  <Typography variant="body2" fontWeight={600} color="success.main">
                    {pendingFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Andere Datei auswählen oder hierher ziehen
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Datei hierher ziehen oder klicken
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Alle Dateitypen unterstützt
                  </Typography>
                </>
              )}
            </Box>

            {error && <Typography color="error" variant="body2">{error}</Typography>}

            <TextField
              label="Beschreibung"
              multiline
              rows={3}
              fullWidth
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optionale Beschreibung zur Datei..."
            />

            {/* Objektzuweisung */}
            <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                Objekt zuweisen
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {OBJECT_TYPES.map((ot) => (
                  <Chip
                    key={ot.value}
                    icon={ot.icon}
                    label={ot.label}
                    clickable
                    onClick={() => setSelectedObjectType((prev) => prev === ot.value ? '' : ot.value)}
                    variant={selectedObjectType === ot.value ? 'filled' : 'outlined'}
                    sx={{
                      borderColor: selectedObjectType === ot.value ? ot.color : 'divider',
                      backgroundColor: selectedObjectType === ot.value ? ot.color : 'transparent',
                      color: selectedObjectType === ot.value ? 'white' : 'text.primary',
                      '& .MuiChip-icon': { color: selectedObjectType === ot.value ? 'white' : ot.color },
                      fontWeight: selectedObjectType === ot.value ? 600 : 400,
                      transition: 'all 0.15s ease',
                    }}
                  />
                ))}
              </Box>

              {selectedObjectType && (
                <FormControl fullWidth>
                  <InputLabel>
                    {OBJECT_TYPES.find((t) => t.value === selectedObjectType)?.label} auswählen
                  </InputLabel>
                  <Select
                    value={selectedObjectId}
                    onChange={(e) => setSelectedObjectId(e.target.value)}
                    label={`${OBJECT_TYPES.find((t) => t.value === selectedObjectType)?.label} auswählen`}
                    disabled={objectListLoading}
                  >
                    {objectListLoading && <MenuItem disabled value="">Lädt...</MenuItem>}
                    {!objectListLoading && objectList.length === 0 && <MenuItem disabled value="">Keine Einträge gefunden</MenuItem>}
                    {objectList.map((obj) => (
                      <MenuItem key={obj.id} value={obj.id}>{getObjectLabel(obj)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>

          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogCancel}>Abbrechen</Button>
          <Button
            onClick={handleDialogConfirm}
            variant="contained"
            disabled={!pendingFile || uploading}
          >
            {uploading ? 'Wird hochgeladen...' : 'Hochladen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit-Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Datei bearbeiten</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {editingAttachment && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {fileIconMap[getFileIcon(getMimeType(editingAttachment.name))]}
                <Typography variant="body2" color="text.secondary">
                  {editingAttachment.name}
                </Typography>
              </Box>
            )}
            <TextField
              label="Beschreibung"
              multiline
              rows={4}
              fullWidth
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder="Beschreibung zur Datei..."
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditCancel}>Abbrechen</Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            disabled={editSaving}
          >
            {editSaving ? 'Wird gespeichert...' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

    </Grid>
  );
}